<template>
  <div class="flex flex-col justify-center min-h-screen bg-light-screen-background dark:bg-dark-screen-background px-4">
    <div class="w-full max-w-md mx-auto">
      <!-- Logo and Title -->
      <div class="flex flex-col items-center mb-8">
        <span @click="$router.push('/')">
          <LogoText :dark-mode="$store.state.ui.darkMode" />
        </span>
        <h2 class="mt-4 text-2xl font-semibold text-center text-light-body-text dark:text-dark-body-text">
          {{ $t("Welcome Back") }}
        </h2>
        <p class="text-sm text-center text-gray-500 dark:text-gray-400">
          {{ $t("Sign in to continue") }}
        </p>
      </div>

      <!-- User Session List -->
      <div v-if="$store.state.auth.user_sessions.length" class="mb-6">
        <h3 class="text-lg font-semibold">
          {{ $t("Your Accounts") }}
        </h3>
        <ul role="list" class="space-y-3 mt-2">
          <li
            v-for="user_session in $store.state.auth.user_sessions"
            :key="user_session.id"
            class="flex items-center gap-3 p-3 border rounded-lg cursor-pointer bg-white hover:bg-gray-100 dark:hover:bg-gray-800"
            @click="sessionLogin(user_session)">
            <Avatar :first-name="user_session.first_name" :last-name="user_session.last_name" :radius="40" />
            <div>
              <p class="text-sm font-medium text-gray-900 dark:text-white">
                {{ user_session.full_name }}
              </p>
              <p class="text-xs text-gray-500">
                {{ user_session.email }}
              </p>
            </div>
          </li>
        </ul>
        <button
          type="button"
          class="w-full py-2 mt-4 text-sm font-semibold text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800"
          @click="logoutAllAccounts">
          {{ $t("Logout from All Accounts") }}
        </button>
      </div>

      <!-- Sign-In Form -->
      <Form
        :initial-values="initValues"
        :validation-schema="schema"
        class="space-y-4 bg-white dark:bg-gray-900 p-6 rounded-lg shadow-lg"
        @submit="onSubmit">
        <TextInput name="email" type="email" label="E-mail" autocomplete="on" />
        <TextInput name="password" type="password" label="Password" class="mb-2" />

        <div v-if="credentials_error" class="text-sm text-red-600 mt-1">
          {{ $t("Invalid credentials. Please check your login details.") }}
        </div>

        <button type="submit" class="w-full py-2 mt-5 text-base font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700">
          {{ $t("Sign In") }}
        </button>
        <!-- Links Section -->
        <div class="flex flex-col items-center justify-between mt-4 gap-2">
          <router-link to="recover-password" class="text-sm font-medium text-blue-600 hover:underline dark:text-blue-400">
            {{ $t("Forgot your password?") }}
          </router-link>
          <!-- <router-link to="start" class="text-sm font-medium text-blue-600 hover:underline dark:text-blue-400">
            {{ $t("Don't have an account? Create account") }}
          </router-link> -->
        </div>
      </Form>
    </div>
  </div>
</template>

<script>
import * as Yup from "yup"
import { ref } from "vue"
import { Form } from "vee-validate"
import TextInput from "@/components/shared/TextInput.vue"
import Avatar from "@/components/shared/Avatar.vue"
import LogoText from "@/components/shared/LogoText.vue"
import dataService from "@/services/data.service"

export default {
  name: "Login",
  components: { Avatar, LogoText, TextInput, Form },
  setup() {
    const credentials_error = ref(false)
    const schema = Yup.object().shape({
      email: Yup.string().email("Email must be a valid email address").required("Email address is required"),
      password: Yup.string().min(8, "Password must be at least 8 characters long").required("Password is required")
    })

    const initValues = { email: "", password: "" }

    return { initValues, schema, credentials_error }
  },
  created() {
    this.$store.dispatch("ui/hideLoading")
  },
  methods: {
    sessionLogin(user_session) {
      const session_token = new URL(user_session.session_link).searchParams.get("token")
      this.$store
        .dispatch("auth/sessionLogin", session_token)
        .then(() => this.$router.replace("/"))
        .catch(() => this.$router.replace("/login"))
    },

    onSubmit(values, { resetForm }) {
      this.$store.dispatch("ui/showLoading")
      this.$store
        .dispatch("auth/login", values)
        .then((res) => {
          this.$gtag.event("login", { event_category: "user_engagement", event_label: "User Login", ...res, value: 1 })
          resetForm()
          this.handleUserIdentification(res)
          this.fetchOrganizationsAndResources()
          this.$store.dispatch("ui/hideLoading")
        })
        .catch(() => {
          this.$store.dispatch("ui/hideLoading")
          this.credentials_error = true
        })
    },
    handleUserIdentification(user) {
      if (window.hj) {
        window.hj("identify", user.email, { env: import.meta.env.VITE_NODE_ENV, user_id: user.id })
      }
      if (localStorage.getItem("portfolio_dismiss_onboarding_video_till_next_login")) {
        localStorage.setItem("is_next_login", true)
      }
    },
    fetchOrganizationsAndResources() {
      dataService.getOrganizations().then((res) => {
        const organization =
          JSON.parse(localStorage.getItem("organization")) ||
          res.data.find((org) => org.users.some((user) => user.id === this.$store.state.auth.user.id))
        this.$store.commit("setOrganization", organization)
        this.$store.dispatch("getInitialResources").then(() => this.$router.replace("/"))
      })
    },
    logoutAllAccounts() {
      this.$store.dispatch("auth/logoutAllAccounts", this.$router)
    }
  }
}
</script>
